import { useEffect, useRef, useState } from 'react';
import {
  CREATE_FEE,
  DEFAULT_SERVER_SIZE,
  formatMoney,
  GAME_DEFINITIONS,
  SERVER_NAME,
  SERVER_NAME_MAX,
  SERVER_SIZES,
  SIZE_SPECS,
  type GameVersion,
  type GameVersionsResponse,
  type ListMyServersResponse,
  type MeResponse,
  type ServerOperationResult,
  type CreateUploadResponse,
  type ServerSize,
} from '@hearth/shared';
import type { Api } from '../api';
import { linkTo, navigate } from '../router';
import { errorText, rateText, sizeText, timeAgo } from './serverWords';
import { GrassBlockIcon, TreeIcon, UploadIcon } from './ChoiceIcons';
import { fileProblem, fileSize, sendForm, UploadCancelled, uploadedText, UploadError, waitForCheck, WorldRefused } from './upload';

// /app/new: create a server (M10.8), as the canvas board: game, name, world, size (M10.7),
// version, then a summary with "Create and wake". Bringing your own world (M10.16) uploads it,
// waits for the check, then creates the server from it.

const GAME = 'minecraft-java';
/** How many versions the list shows at first, and adds each time it's scrolled near the end. */
export const VERSION_PAGE = 12;
const NEW_DAYS = 30;

export type World = 'fresh' | 'upload';
/** Where creating from an upload has got to. */
export type Stage = 'uploading' | 'checking' | 'creating';

/** Why a name won't do, or '' if it will (the API's own rule: SERVER_NAME). */
export function nameProblem(name: string): string {
  if (!name.trim()) return 'Give it a name.';
  if ([...name].length > SERVER_NAME_MAX) return `At most ${SERVER_NAME_MAX} characters.`;
  if (!SERVER_NAME.test(name)) return 'No spaces at the start or end.';
  return '';
}

/** A release from the last 30 days is flagged New. */
export const isNew = (v: GameVersion, now: Date) => now.getTime() - Date.parse(v.releasedAt) < NEW_DAYS * 86_400_000;

/** "2 weeks ago" for recent releases, "March 2024" for older ones. */
export function releasedWhen(releasedAt: string, now: Date): string {
  if (now.getTime() - Date.parse(releasedAt) < 60 * 86_400_000) return timeAgo(releasedAt, now);
  return new Date(releasedAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/** Whether the person may create one now: approved (admins needn't be) and under their limit. */
export function mayCreate(me: MeResponse, owned: number): 'yes' | 'waiting' | 'limit' {
  if (me.admin) return 'yes';
  if (!me.approved) return 'waiting';
  return owned >= me.serverLimit ? 'limit' : 'yes';
}

export function CreatePage({ api, me }: { api: Api; me: MeResponse }) {
  const [owned, setOwned] = useState<number>();
  const [versions, setVersions] = useState<GameVersion[]>();
  const [name, setName] = useState('');
  const [version, setVersion] = useState<string>();
  const [size, setSize] = useState<ServerSize>(DEFAULT_SERVER_SIZE);
  // The game's EULA: unticked until the owner ticks it, and required (the API refuses a create without it).
  const [agreed, setAgreed] = useState(false);
  const [shown, setShown] = useState(VERSION_PAGE);
  const [touched, setTouched] = useState(false);
  const [world, setWorld] = useState<World>('fresh');
  const [file, setFile] = useState<File>();
  const [stage, setStage] = useState<Stage>();
  const [progress, setProgress] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  /** Why the world check refused the last upload: shown in the World step until another file is chosen. */
  const [refused, setRefused] = useState('');
  const [error, setError] = useState('');
  const cancel = useRef<AbortController>(undefined);
  const busy = stage !== undefined;

  // Leaving mid-upload would lose it: the browser asks first.
  useEffect(() => {
    if (!busy) return;
    const stay = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', stay);
    return () => window.removeEventListener('beforeunload', stay);
  }, [busy]);

  useEffect(() => {
    api.get<ListMyServersResponse>('/v1/servers').then((r) => setOwned(r.servers.filter((s) => s.role === 'owner').length), (err) => setError(errorText(err)));
    api.get<GameVersionsResponse>(`/v1/games/${GAME}/versions`).then((r) => setVersions(r.versions), (err) => setError(errorText(err)));
  }, [api]);

  async function create() {
    setTouched(true);
    const chosen = version ?? versions?.[0]?.version;
    const uploading = world === 'upload';
    if (nameProblem(name) || !chosen || !agreed || (uploading && (!file || fileProblem(file)))) return;
    setError('');
    setRefused('');
    try {
      let upload: string | undefined;
      if (uploading && file) {
        setStage('uploading');
        setProgress(0);
        setStartedAt(Date.now());
        cancel.current = new AbortController();
        const form = await api.post<CreateUploadResponse>('/v1/uploads', { game: GAME });
        await sendForm(form, file, setProgress, cancel.current.signal);
        setStage('checking');
        await waitForCheck(api, form.uploadId);
        upload = form.uploadId;
      }
      setStage('creating');
      const created = await api.post<ServerOperationResult>('/v1/servers', { name, game: GAME, version: chosen, size, acceptEula: true, ...(upload ? { upload } : {}) });
      navigate(`/app/servers/${encodeURIComponent(created.serverId)}`);
    } catch (err) {
      // A refused world is cleared, so trying again means choosing a file again (not re-sending this one).
      if (err instanceof WorldRefused) {
        setRefused(err.reason);
        setFile(undefined);
      } else if (!(err instanceof UploadCancelled)) {
        setError(err instanceof UploadError ? err.message : errorText(err));
      }
      setStage(undefined);
    }
  }

  return (
    <CreateView
      me={me}
      owned={owned}
      versions={versions}
      name={name}
      version={version ?? versions?.[0]?.version}
      size={size}
      agreed={agreed}
      onAgree={setAgreed}
      world={world}
      file={file}
      stage={stage}
      progress={progress}
      shown={shown}
      touched={touched}
      busy={busy}
      error={error}
      now={new Date()}
      onName={setName}
      onVersion={setVersion}
      onSize={setSize}
      onWorld={setWorld}
      onFile={(chosen) => {
        setFile(chosen);
        setRefused('');
      }}
      refused={refused}
      elapsedMs={stage === 'uploading' ? Date.now() - startedAt : 0}
      onCancel={() => cancel.current?.abort()}
      onMore={() => setShown((n) => n + VERSION_PAGE)}
      onCreate={() => void create()}
    />
  );
}

/** The create page, given what's loaded and chosen (no fetching: easy to test). */
export function CreateView(props: {
  me: MeResponse;
  owned: number | undefined;
  versions: GameVersion[] | undefined;
  name: string;
  version: string | undefined;
  size: ServerSize;
  /** Ticked the game's EULA. */
  agreed: boolean;
  onAgree: (agreed: boolean) => void;
  world: World;
  /** The file chosen to upload. */
  file: { name: string; size: number } | undefined;
  stage?: Stage;
  /** How much of the file has gone, 0–1, while uploading. */
  progress?: number;
  /** How long the upload has been going, for the time left. */
  elapsedMs?: number;
  /** Why the world check refused the last upload, if it did. */
  refused?: string;
  shown: number;
  touched: boolean;
  busy: boolean;
  error: string;
  now: Date;
  onName: (name: string) => void;
  onVersion: (version: string) => void;
  onSize: (size: ServerSize) => void;
  onWorld: (world: World) => void;
  onFile: (file: File | undefined) => void;
  onCancel?: () => void;
  onMore: () => void;
  onCreate: () => void;
}) {
  const { me, owned, versions, name, version, size, agreed, onAgree, world, file, stage, progress = 0, elapsedMs = 0, refused = '', shown, touched, busy, error, now, onName, onVersion, onSize, onWorld, onFile, onCancel, onMore, onCreate } = props;
  const uploading = world === 'upload';
  const badFile = file ? fileProblem(file) : touched && uploading ? "Choose your world's file." : '';
  const game = GAME_DEFINITIONS[GAME];
  const problem = touched ? nameProblem(name) : '';
  const allowed = owned === undefined ? undefined : mayCreate(me, owned);
  const list = versions?.slice(0, shown) ?? [];

  return (
    <>
      <a className="back" {...linkTo('/app')}>← Servers</a>
      <h1 className="page-title">New server</h1>
      {error && <p className="notice" role="alert">{error}</p>}
      {allowed === 'waiting' ? (
        <section className="panel create-stop">
          <h2>Waiting for approval</h2>
          <p>You're signed in, but someone running Hearth needs to approve your account before you can create servers. You can still play on servers friends invite you to.</p>
          <a className="btn" {...linkTo('/app')}>Back to servers</a>
        </section>
      ) : allowed === 'limit' ? (
        <section className="panel create-stop">
          <h2>{`You have ${owned} servers already`}</h2>
          <p>That's the most you can have for now. Destroy one you don't play any longer to make room; its backups are kept.</p>
          <a className="btn" {...linkTo('/app')}>Your servers</a>
        </section>
      ) : !allowed || !versions ? (
        !error && <p className="quiet" aria-live="polite">Loading…</p>
      ) : (
        <div className="server-panels">
          <div className="create-steps">
            <section className="panel">
              <h2>1 · Game</h2>
              {/* One game for now: shown chosen. A second (Hytale) makes these a real choice. */}
              <div className="choices">
                <div className="choice on with-icon">
                  <GrassBlockIcon />
                  <span>
                    <strong>{game.displayName}</strong>
                    <span className="quiet">{`Versions ${versions.at(-1)?.version ?? ''} to the newest`}</span>
                  </span>
                </div>
                <div className="choice choice-soon" aria-hidden="true">More games coming</div>
              </div>
            </section>
            <section className="panel">
              <h2><label htmlFor="server-name">2 · Name</label></h2>
              <input
                id="server-name"
                className="field"
                type="text"
                placeholder="Friday Night SMP"
                maxLength={SERVER_NAME_MAX}
                value={name}
                aria-invalid={problem ? 'true' : undefined}
                aria-describedby={problem ? 'name-problem' : undefined}
                onChange={(e) => onName(e.target.value)}
              />
              {problem && <p id="name-problem" className="field-problem">{problem}</p>}
            </section>
            <section className="panel">
              <h2 id="world-step">3 · World</h2>
              <div role="radiogroup" aria-labelledby="world-step" className="choices">
                <button className={uploading ? 'choice with-icon' : 'choice on with-icon'} type="button" role="radio" aria-checked={!uploading} disabled={busy} onClick={() => onWorld('fresh')}>
                  <TreeIcon />
                  <span>
                    <strong>A fresh world</strong>
                    <span className="quiet">Generate a brand new world</span>
                  </span>
                </button>
                <button className={uploading ? 'choice on with-icon' : 'choice with-icon'} type="button" role="radio" aria-checked={uploading} disabled={busy} onClick={() => onWorld('upload')}>
                  <UploadIcon />
                  <span>
                    <strong>Bring your world</strong>
                    <span className="quiet">Upload a save you already play</span>
                  </span>
                </button>
              </div>
              {uploading && refused && (
                <p className="notice" role="alert">
                  {`Invalid World Upload: ${refused}`}
                </p>
              )}
              {uploading && (
                <div
                  className="dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (!busy && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]);
                  }}
                >
                  {file ? (
                    <p>
                      <strong>{file.name}</strong>
                      {` · ${fileSize(file.size)}`}
                    </p>
                  ) : (
                    <p>
                      Drop your world's <strong>.zip</strong> or <strong>.tar.gz</strong> here
                    </p>
                  )}
                  {!busy && (
                    <label className={file ? 'quiet-btn' : 'btn'}>
                      {file ? 'Choose another file' : 'Choose a file'}
                      <input className="sr-only" type="file" accept=".zip,.tar.gz,.tgz" onChange={(e) => onFile(e.target.files?.[0])} />
                    </label>
                  )}
                  {!file && <p className="quiet">The world folder (the one with level.dat), up to 4 GB.</p>}
                  {badFile && <p className="field-problem">{badFile}</p>}
                </div>
              )}
            </section>
            <section className="panel">
              <h2 id="size-step">4 · How many players?</h2>
              <p className="quiet">You can change it later, while it's asleep.</p>
              <div role="radiogroup" aria-labelledby="size-step" className="choices sizes">
                {SERVER_SIZES.map((s) => (
                  <button key={s} className={s === size ? 'choice on' : 'choice'} type="button" role="radio" aria-checked={s === size} onClick={() => onSize(s)}>
                    <strong>{SIZE_SPECS[s].name}</strong>
                    <span className="quiet">{SIZE_SPECS[s].players}</span>
                    <span className="size-rate">{rateText(s)}</span>
                  </button>
                ))}
              </div>
            </section>
            <section className="panel">
              <h2>5 · Version</h2>
              <p className="quiet">
                {uploading ? "Pick the version your world was last played on, or a newer one. Friends' Minecraft needs to match it." : "The newest is picked for you. Friends' Minecraft needs to match it."}
              </p>
              <div
                role="radiogroup"
                aria-label="Minecraft version"
                className="versions"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  if (shown < versions.length && el.scrollTop + el.clientHeight >= el.scrollHeight - 48) onMore();
                }}
              >
                {list.map((v) => (
                  <button key={v.version} className={v.version === version ? 'version on' : 'version'} type="button" role="radio" aria-checked={v.version === version} onClick={() => onVersion(v.version)}>
                    <span>
                      {`Minecraft ${v.version}`}
                      {isNew(v, now) && <span className="new">New</span>}
                    </span>
                    <span className="quiet">{releasedWhen(v.releasedAt, now)}</span>
                  </button>
                ))}
                <p className="quiet versions-note">{shown < versions.length ? 'Scroll for older versions.' : `That's every version back to ${versions.at(-1)?.version}.`}</p>
              </div>
            </section>
          </div>
          <aside className="panel create-summary">
            <h2>Your server</h2>
            <dl className="details">
              {[
                ['Game', game.displayName],
                ['Name', name.trim() || '—'],
                ['World', uploading ? (file?.name ?? 'Your upload') : 'A fresh world'],
                ['Size', sizeText(size)],
                ['Price', `${formatMoney(CREATE_FEE)} to create, then ${rateText(size)} while awake`],
                ['Version', version ? `Minecraft ${version}` : '—'],
                ['Falls asleep', 'After 30 minutes with nobody on'],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            {stage === 'uploading' ? (
              <div className="upload-progress" aria-live="polite">
                <strong>Uploading your world</strong>
                <progress max={1} value={progress} aria-label="Uploading your world" />
                <p className="quiet">{uploadedText(progress, file?.size ?? 0, elapsedMs)}</p>
                <p>Please stay on this page until it's done.</p>
                {onCancel && (
                  <button className="quiet-btn" type="button" onClick={onCancel}>
                    Cancel upload
                  </button>
                )}
              </div>
            ) : stage === 'checking' ? (
              <p className="upload-wait" role="status">
                Validating your world files. Please stay on this page.
              </p>
            ) : (
              <>
                <label className="check">
                  <input type="checkbox" checked={agreed} disabled={busy} onChange={(e) => onAgree(e.target.checked)} aria-describedby={touched && !agreed ? 'eula-problem' : undefined} />
                  <span>
                    I agree to the{' '}
                    <a href={game.eula.url} target="_blank" rel="noreferrer">
                      {game.eula.name}
                    </a>
                  </span>
                </label>
                {touched && !agreed && <p id="eula-problem" className="field-problem">Agree to the {game.eula.name} to create your server.</p>}
                <button className="btn btn-primary btn-tall" type="button" disabled={busy} onClick={onCreate}>
                  {busy ? 'Creating…' : 'Create and wake'}
                </button>
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
